/**
 * Words for a roll's booked-in details and where it is, for the admin pages.
 * Pure, so the rules are tested here rather than through the DOM.
 */
import type { NegativesPlan, NegativesState, Roll, RollFormat, RollKind, RollProcess } from '../types/roll';

export const KIND_LABELS: Record<Exclude<RollKind, 'unknown'>, string> = {
  develop_and_scan: 'Develop and scan',
  develop_only: 'Develop only',
  scan_only: 'Scan only',
};

export const FORMAT_LABELS: Record<Exclude<RollFormat, 'other'>, string> = {
  '35mm': '35mm',
  half_frame: 'Half frame',
  '120': '120',
};

export const PROCESS_LABELS: Record<RollProcess, string> = {
  c41: 'C-41',
  bw: 'B&W',
  e6: 'E-6',
  other: 'Other',
};

export const PLAN_LABELS: Record<Exclude<NegativesPlan, 'unknown'>, string> = {
  post_back: 'Post back',
  hold: 'Hold for a companion print or film order',
  collect: 'Collect from the lab',
  dispose: 'Dispose of',
};

/** The film stock id for unknown or unbranded film (loupe-core `UNKNOWN_FILM_STOCK_ID`). */
export const UNKNOWN_FILM_STOCK_ID = 'unknown';

/**
 * Whether a roll of this format needs its lab number at booking in: every 35mm
 * roll (half frame too) carries a twin-check label; 120 doesn't yet. Mirrors
 * loupe-core `tracking::lab_number_required`.
 */
export function labNumberRequired(format: RollFormat | '' | null | undefined): boolean {
  return format !== '120';
}

/**
 * The form a lab number is compared in, as the server does: upper case, and
 * an all-digit number without leading zeros ("0412" is 412). Null when it
 * isn't a valid number: up to 16 letters, digits and dashes.
 */
export function normaliseLabNumber(typed: string): string | null {
  const number = typed.trim().toUpperCase();
  if (!/^[A-Z0-9-]{1,16}$/.test(number)) return null;
  return /^\d+$/.test(number) ? String(Number.parseInt(number, 10)) : number;
}

/** "Kodak Portra 400", "Unknown film", or null before the film is known. */
export function filmLabel(roll: Pick<Roll, 'film_stock_id' | 'film_brand' | 'film_stock_name'>): string | null {
  if (!roll.film_stock_id) return null;
  if (roll.film_brand && roll.film_stock_name) return `${roll.film_brand} ${roll.film_stock_name}`;
  return roll.film_stock_name ?? 'Unknown film';
}

/** "Colour C-41 · 35mm · Develop and scan": a Shopify line or a roll, briefly. */
export function describeRoll(details: { kind?: RollKind | null; format?: RollFormat | null; process?: RollProcess | null }): string {
  return [
    details.process ? PROCESS_LABELS[details.process] : null,
    details.format && details.format !== 'other' ? FORMAT_LABELS[details.format] : null,
    details.kind && details.kind !== 'unknown' ? KIND_LABELS[details.kind] : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function shortDate(iso: string): string {
  return DAY.format(new Date(iso));
}

/** How far through the lab a roll is: the last step it has done. */
export function stageLabel(roll: Pick<Roll, 'tracking'>): string | null {
  const done = roll.tracking?.steps.filter((s) => s.state === 'done' && s.key !== 'negatives') ?? [];
  const last = done[done.length - 1];
  if (!last) return null;
  return { received: 'Received', developed: 'Developed', scans_ready: 'Scans ready', negatives: '' }[last.key];
}

const PENDING: Record<NegativesPlan, string> = {
  post_back: 'will be posted back',
  hold: 'will be held for a companion print or film order',
  collect: 'will be collected',
  dispose: 'will be disposed of',
  unknown: 'plan not known',
};

/** What the negatives' status says, with the parcel to track when there is one. */
export interface NegativesSummary {
  text: string;
  /** Needs doing or overdue: shown as a warning. */
  attention: boolean;
  trackingUrl?: string;
}

/** Where a roll's negatives are, in a few words. */
export function describeNegatives(state: NegativesState): NegativesSummary {
  switch (state.state) {
    case 'pending':
      return { text: PENDING[state.plan] ?? PENDING.unknown, attention: false };
    case 'awaiting_dispatch':
      return { text: 'to post back', attention: true };
    case 'posted': {
      const via = [state.parcel.carrier, state.parcel.number].filter(Boolean).join(' ');
      return {
        text: `posted back ${shortDate(state.parcel.shipped_at)}${via ? ` · ${via}` : ''}`,
        attention: false,
        trackingUrl: state.parcel.url,
      };
    }
    case 'held':
      return {
        text: state.overdue ? `hold ended ${shortDate(state.until)}: dispose of them` : `held until ${shortDate(state.until)}`,
        attention: state.overdue,
      };
    case 'ready_to_collect':
      return { text: 'ready to collect', attention: false };
    case 'collected':
      return { text: `collected ${shortDate(state.at)}`, attention: false };
    case 'awaiting_disposal':
      return { text: 'to dispose of', attention: true };
    case 'disposed':
      return { text: `disposed of ${shortDate(state.at)}`, attention: false };
    case 'returned_with_order':
      return { text: 'sent with another order', attention: false };
    default:
      return { text: 'plan not known', attention: false };
  }
}

/**
 * The lab numbers "Number the rest" would give: counting on from the first
 * all-digit number, each later roll that needs a number and has none gets the
 * next, keeping the label's zero padding ("0412" → "0413"). Rolls that
 * already have a number, and 120 rolls, are left alone. Null when there's
 * nothing to fill.
 */
export function numberTheRest<T extends { number: string; format: RollFormat | '' }>(
  rows: T[],
): { from: string; numbers: (string | null)[] } | null {
  const start = rows.findIndex((r) => /^\d+$/.test(r.number.trim()));
  if (start < 0) return null;
  const first = rows[start].number.trim();
  const width = first.length;
  let next = Number.parseInt(first, 10);
  const numbers = rows.map((row, i) => {
    if (i <= start || row.number.trim() !== '' || !labNumberRequired(row.format)) return null;
    next += 1;
    return String(next).padStart(width, '0');
  });
  const firstFilled = numbers.find((n) => n !== null);
  return firstFilled ? { from: firstFilled, numbers } : null;
}

/** What's wrong with a roll's details before they're sent, by field. */
export interface RollFieldProblems {
  number?: string;
  stock?: string;
  format?: string;
}

/**
 * The server's checks, made first so the form can say what's wrong beside
 * each field: a format, a film stock, and a valid lab number, which 35mm
 * can't go without.
 */
export function checkRollFields(fields: { number: string; stock: unknown; format: RollFormat | '' }): RollFieldProblems {
  const problems: RollFieldProblems = {};
  if (!fields.format) problems.format = 'Choose the format';
  if (!fields.stock) problems.stock = 'Choose the film stock, or Unknown / unbranded';
  const typed = fields.number.trim();
  if (typed && normaliseLabNumber(typed) === null) {
    problems.number = 'Use up to 16 letters, digits and dashes';
  } else if (!typed && fields.format && labNumberRequired(fields.format)) {
    problems.number = `Every ${fields.format === 'half_frame' ? 'half frame' : '35mm'} roll needs its lab number`;
  }
  return problems;
}

/**
 * Whether the lab has finished with a roll, from its steps: every step
 * before the negatives is done. Its negatives can then be dealt with.
 */
export function labIsDone(roll: Pick<Roll, 'tracking'>): boolean {
  const steps = roll.tracking?.steps.filter((s) => s.key !== 'negatives') ?? [];
  return steps.length > 0 && steps.every((s) => s.state === 'done');
}
